import { PublicSupport } from "../components/PublicSupport";
import { ownerStorage } from "../lib/privacy/ownerStorage";
import { useLayoutEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useTenant } from "./TenantContext";
import { ConsentProvider } from "./ConsentContext";
import { AppProvider } from "./AppContext";
import { EventsProvider } from "./EventsContext";
import { PrivacyGate } from "../components/PrivacyGate";
import { SaveStatus } from "../components/SaveStatus";
import { syncService } from "../lib/syncService";
import { useConsent } from "./ConsentContext";
function PrivateApp({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { hasConsent, needsAgeVerification } = useConsent();
  return (
    <EventsProvider
      config={{
        tenantId: tenant.id,
        userId: user?.id,
        enabled:
          !needsAgeVerification && hasConsent("institution_aggregate_only"),
        consentScope: hasConsent("institution_aggregate_only")
          ? "institution_aggregate_only"
          : "personal_only",
        syncEndpoint: "/api/events/ingest",
      }}
    >
      <PrivacyGate>
        <AppProvider>
          <SaveStatus />
          {children}
        </AppProvider>
      </PrivacyGate>
    </EventsProvider>
  );
}
export function SessionBoundary({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { tenant, isLoading: tenantLoading } = useTenant();
  const fingerprint = `${tenant.id}:${user?.id || ""}`;
  const [ready, setReady] = useState("");
  useLayoutEffect(() => {
    if (!user || isLoading || tenantLoading) return;
    let remember = false;
    try {
      remember =
        !user.isAnonymous &&
        localStorage.getItem(`rf:remember:${tenant.id}:${user.id}`) === "true";
    } catch {
      /* Session mode remains available. */
    }
    syncService.configureScope(tenant.id, remember);
    setReady(fingerprint);
    if (isAuthenticated && syncService.isCloudEnabled()) {
      const saved = ownerStorage.getItem("resilience_forge_app_state");
      if (saved) {
        try {
          void syncService.saveSnapshot(JSON.parse(saved));
        } catch {
          /* Recovery remains local. */
        }
      }
    }
  }, [
    fingerprint,
    isLoading,
    tenantLoading,
    isAuthenticated,
    tenant.id,
    user?.id,
  ]);
  if (isLoading || tenantLoading || !user || ready !== fingerprint)
    return (
      <main className="privacy-welcome">
        <p role="status">Opening your session…</p>
        <p>
          You can notice a neutral object nearby while you wait. No private
          practice is recorded here.
        </p>
        <PublicSupport />
      </main>
    );
  // Keying destroys all old reducer state, drafts, timers, dialogs, and subscriptions on identity changes.
  return (
    <ConsentProvider key={fingerprint} userId={user.id}>
      <PrivateApp>{children}</PrivateApp>
    </ConsentProvider>
  );
}
